import type { ServiceHubType } from '../constants/serviceHubs';

export type ScheduleType = 'ride_now' | 'ride_later' | 'default';

export interface ZonePricingInput {
  baseFare?: number;
  baseDistanceKm?: number;
  perKmRate?: number;
  perMinRate?: number;
  perKgRate?: number;
  perHour?: number;
  perDay?: number;
  deliveryCharge?: number;
  minimumFare?: number;
  freeWaitingMin?: number;
  waitingChargePerMin?: number;
  driverAllowance?: number;
  roundTripMultiplier?: number;
}

export interface FareCalcInput {
  hubType: ServiceHubType | string;
  slug?: string;
  pricing: ZonePricingInput;
  distanceKm?: number;
  durationMin?: number;
  weightKg?: number;
  hours?: number;
  days?: number;
  waitingMin?: number;
  surgeMultiplier?: number;
  surgePerKm?: number;
  outOfZoneFee?: number;
  taxRate?: number;
  tripMode?: string;
}

export function calculateZoneSubtotal(input: FareCalcInput): number {
  return calculateZoneFareBreakdown(input).subtotal;
}

export interface FareBreakdownLine {
  id: string;
  label: string;
  formula: string;
  amount: number;
}

export interface ZoneFareBreakdownResult {
  lines: FareBreakdownLine[];
  billableKm: number;
  subtotalBeforeMinimum: number;
  minimumFare: number;
  minimumFareApplied: boolean;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Itemised fare lines for preview — mirrors calculateZoneSubtotal. */
export function calculateZoneFareBreakdown(input: FareCalcInput): ZoneFareBreakdownResult {
  const p = input.pricing;
  const distanceKm = input.distanceKm ?? 0;
  const billableKm = Math.max(0, distanceKm - (p.baseDistanceKm ?? 0));
  const durationMin = input.durationMin ?? 0;
  const waitingMin = Math.max(0, (input.waitingMin ?? 0) - (p.freeWaitingMin ?? 0));
  const waitingCharge = roundMoney(waitingMin * (p.waitingChargePerMin ?? 0));
  const lines: FareBreakdownLine[] = [];
  const hub = input.hubType;
  let coreSubtotal = 0;

  if (hub === 'transport') {
    const slug = input.slug || '';
    if (slug.includes('rental') && slug.includes('bike')) {
      const hours = input.hours ?? 1;
      const baseFare = p.baseFare ?? 0;
      const hourCharge = roundMoney(hours * (p.perHour ?? 0));
      if (baseFare) lines.push({ id: 'base', label: 'Base fare', formula: formatRate(baseFare), amount: baseFare });
      if (hourCharge) lines.push({ id: 'hours', label: 'Hourly charge', formula: `${hours} hr × ${formatRate(p.perHour ?? 0)}`, amount: hourCharge });
      coreSubtotal = roundMoney(baseFare + hourCharge);
    } else if (slug.includes('rental')) {
      const days = input.days ?? 1;
      const baseFare = p.baseFare ?? 0;
      const dayCharge = roundMoney(days * (p.perDay ?? 0));
      if (baseFare) lines.push({ id: 'base', label: 'Base fare', formula: formatRate(baseFare), amount: baseFare });
      if (dayCharge) lines.push({ id: 'days', label: 'Daily charge', formula: `${days} day × ${formatRate(p.perDay ?? 0)}`, amount: dayCharge });
      coreSubtotal = roundMoney(baseFare + dayCharge);
    } else if (slug === 'outstation' || slug === 'intercity') {
      const baseFare = p.baseFare ?? 0;
      const distanceCharge = roundMoney(billableKm * (p.perKmRate ?? 0));
      const allowance = p.driverAllowance ?? 0;
      if (baseFare) lines.push({ id: 'base', label: 'Base fare', formula: formatRate(baseFare), amount: baseFare });
      if (billableKm > 0 && (p.perKmRate ?? 0) > 0) {
        lines.push({
          id: 'distance',
          label: 'Distance charge',
          formula: `${formatKm(billableKm)} km × ${formatRate(p.perKmRate ?? 0)}`,
          amount: distanceCharge,
        });
      }
      if (allowance) lines.push({ id: 'allowance', label: 'Driver allowance', formula: formatRate(allowance), amount: allowance });
      coreSubtotal = roundMoney(baseFare + distanceCharge + allowance);
      if (input.tripMode === 'round_trip') {
        const multiplier = p.roundTripMultiplier ?? 1.8;
        const adjusted = roundMoney(coreSubtotal * multiplier);
        lines.push({ id: 'round_trip', label: 'Round trip multiplier', formula: `× ${multiplier}`, amount: roundMoney(adjusted - coreSubtotal) });
        coreSubtotal = adjusted;
      }
    } else if (slug === 'courier-service') {
      const weightKg = Math.max(input.weightKg ?? 1, 1);
      const baseFare = p.baseFare ?? 0;
      const distanceCharge = roundMoney(billableKm * (p.perKmRate ?? 0));
      const weightCharge = roundMoney(weightKg * (p.perKgRate ?? 0));
      if (baseFare) lines.push({ id: 'base', label: 'Base fare', formula: formatRate(baseFare), amount: baseFare });
      if (billableKm > 0 && (p.perKmRate ?? 0) > 0) {
        lines.push({
          id: 'distance',
          label: 'Distance charge',
          formula: `${formatKm(billableKm)} km × ${formatRate(p.perKmRate ?? 0)}`,
          amount: distanceCharge,
        });
      }
      if (weightCharge) lines.push({ id: 'weight', label: 'Weight charge', formula: `${weightKg} kg × ${formatRate(p.perKgRate ?? 0)}`, amount: weightCharge });
      coreSubtotal = roundMoney(baseFare + distanceCharge + weightCharge);
    } else {
      const baseFare = p.baseFare ?? 0;
      const distanceCharge = roundMoney(billableKm * (p.perKmRate ?? 0));
      const timeCharge = roundMoney(durationMin * (p.perMinRate ?? 0));
      lines.push({ id: 'base', label: 'Base fare', formula: formatRate(baseFare), amount: baseFare });
      lines.push({
        id: 'distance',
        label: 'Distance charge',
        formula: billableKm > 0
          ? `(${formatKm(distanceKm)} − ${formatKm(p.baseDistanceKm ?? 0)}) km × ${formatRate(p.perKmRate ?? 0)}`
          : `0 km billable (within ${formatKm(p.baseDistanceKm ?? 0)} km base)`,
        amount: distanceCharge,
      });
      lines.push({
        id: 'time',
        label: 'Time charge',
        formula: `${durationMin} min × ${formatRate(p.perMinRate ?? 0)}`,
        amount: timeCharge,
      });
      coreSubtotal = roundMoney(baseFare + distanceCharge + timeCharge);
    }
  } else if (hub === 'delivery') {
    const base = p.deliveryCharge ?? p.baseFare ?? 0;
    const distanceCharge = roundMoney(billableKm * (p.perKmRate ?? 0));
    lines.push({ id: 'delivery', label: 'Delivery charge', formula: formatRate(base), amount: base });
    lines.push({
      id: 'distance',
      label: 'Distance charge',
      formula: billableKm > 0
        ? `(${formatKm(distanceKm)} − ${formatKm(p.baseDistanceKm ?? 0)}) km × ${formatRate(p.perKmRate ?? 0)}`
        : `0 km billable (within ${formatKm(p.baseDistanceKm ?? 0)} km base)`,
      amount: distanceCharge,
    });
    coreSubtotal = roundMoney(base + distanceCharge);
  } else {
    const base = p.baseFare ?? p.deliveryCharge ?? 0;
    const timeCharge = roundMoney(durationMin * (p.perMinRate ?? 0));
    if (base) lines.push({ id: 'base', label: 'Base fare', formula: formatRate(base), amount: base });
    if (timeCharge) lines.push({ id: 'time', label: 'Time charge', formula: `${durationMin} min × ${formatRate(p.perMinRate ?? 0)}`, amount: timeCharge });
    coreSubtotal = roundMoney(base + timeCharge);
  }

  if (waitingCharge) {
    lines.push({
      id: 'waiting',
      label: 'Waiting charge',
      formula: `${waitingMin} min × ${formatRate(p.waitingChargePerMin ?? 0)}`,
      amount: waitingCharge,
    });
  }

  let subtotalBeforeMinimum = roundMoney(coreSubtotal + waitingCharge);
  const mult = input.surgeMultiplier ?? 1;
  const surgePerKmCharge = roundMoney(billableKm * (input.surgePerKm ?? 0));

  if (mult !== 1) {
    const surged = roundMoney(subtotalBeforeMinimum * mult);
    lines.push({
      id: 'surge_multiplier',
      label: 'Surge multiplier',
      formula: `× ${mult}`,
      amount: roundMoney(surged - subtotalBeforeMinimum),
    });
    subtotalBeforeMinimum = surged;
  }
  if (surgePerKmCharge) {
    lines.push({
      id: 'surge_per_km',
      label: 'Surge per km',
      formula: `${formatKm(billableKm)} km × ${formatRate(input.surgePerKm ?? 0)}`,
      amount: surgePerKmCharge,
    });
    subtotalBeforeMinimum = roundMoney(subtotalBeforeMinimum + surgePerKmCharge);
  }

  if (input.outOfZoneFee) {
    lines.push({
      id: 'out_of_zone',
      label: 'Out-of-zone fee',
      formula: formatRate(input.outOfZoneFee),
      amount: input.outOfZoneFee,
    });
    subtotalBeforeMinimum = roundMoney(subtotalBeforeMinimum + input.outOfZoneFee);
  }

  const minimumFare = p.minimumFare ?? 0;
  const minimumFareApplied = minimumFare > subtotalBeforeMinimum;
  const subtotal = roundMoney(Math.max(subtotalBeforeMinimum, minimumFare));
  if (minimumFareApplied) {
    lines.push({
      id: 'minimum_fare',
      label: 'Minimum fare applied',
      formula: `floor ${formatRate(minimumFare)}`,
      amount: roundMoney(subtotal - subtotalBeforeMinimum),
    });
  }

  const taxRate = input.taxRate ?? 0.18;
  const tax = roundMoney(subtotal * taxRate);
  const total = roundMoney(subtotal + tax);

  return {
    lines,
    billableKm,
    subtotalBeforeMinimum,
    minimumFare,
    minimumFareApplied,
    subtotal,
    taxRate,
    tax,
    total,
  };
}

function formatRate(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : '0.00';
}

function formatKm(value: number): string {
  return Number.isFinite(value) ? String(roundMoney(value)) : '0';
}

export function calculateZoneFareTotal(input: FareCalcInput) {
  const breakdown = calculateZoneFareBreakdown(input);
  return { subtotal: breakdown.subtotal, tax: breakdown.tax, total: breakdown.total };
}

export const EMPTY_PRICING: ZonePricingInput = {
  baseFare: 0,
  baseDistanceKm: 2,
  perKmRate: 0,
  perMinRate: 0,
  minimumFare: 0,
  freeWaitingMin: 3,
  waitingChargePerMin: 0,
  deliveryCharge: 0,
};
